import { useState } from 'react';
import { useCatalog } from '../data/useCatalog';
import { useDashboardConfig } from '../context/DashboardContext';
import OutcomesTab from './OutcomesTab';
import CustomizeTab from './CustomizeTab';
import ThemeTab from './ThemeTab';
import IntroTab from './IntroTab';

/**
 * Combined settings modal — Outcomes tab + Customize tab.
 * Owns all draft state for both tabs and the catalog fetch (so catalog
 * doesn't re-fetch when switching between tabs).
 */
export default function DashboardSettings({
  initialTab,
  // Outcomes props
  selectedIndicators,
  onOutcomesSave,
  // Customize props
  indicators,
  currentEnabled, currentGoals, currentGradients, currentRationales,
  currentYearRange, allYears, currentThemes,
  onCustomizeSave,
  // Theme props
  currentBrandTheme, currentCustomBranding,
  onThemeSave,
  // Intro screen props
  currentIntroContent,
  onIntroSave,
  // Shared
  onClose
}) {
  const config = useDashboardConfig();
  const [activeTab, setActiveTab] = useState(initialTab ?? 'outcomes');
  const [exportCopied, setExportCopied] = useState(false);

  // Catalog fetched once here — not inside OutcomesTab — so it survives tab switches
  const { census, cdc, svi, ejscreen, education, loading: catalogLoading, censusError, cdcError } = useCatalog();

  // ── Outcomes draft ──────────────────────────────────────────────────────────
  const [outcomeDraft, setOutcomeDraft] = useState(selectedIndicators);

  // ── Customize draft (mirrors AdminPanel local state) ────────────────────────
  const [selected, setSelected] = useState(new Set(currentEnabled));
  const [goalInputs, setGoalInputs] = useState(() => ({ ...currentGoals }));
  const [gradientSelections, setGradientSelections] = useState(() => ({ ...currentGradients }));
  const [rationaleInputs, setRationaleInputs] = useState(() => ({ ...currentRationales }));
  const [yearRange, setYearRange] = useState(() => ({ ...currentYearRange }));
  const [themes, setThemes] = useState(() =>
    currentThemes.map(t => ({ ...t, indicatorIds: [...t.indicatorIds] }))
  );

  // ── Theme draft ──────────────────────────────────────────────────────────────
  const [draftBrandTheme, setDraftBrandTheme] = useState(currentBrandTheme ?? 'fcp');
  const [draftCustomBranding, setDraftCustomBranding] = useState(() =>
    currentCustomBranding ?? { primaryColor: '#2d6b49', accentColor: '#c8614a', backgroundColor: '#f0fbff', logoDataUrl: null }
  );

  // ── Intro screen draft ────────────────────────────────────────────────────
  const [draftIntro, setDraftIntro] = useState(() =>
    currentIntroContent ?? { enabled: false, title: '', body: '' }
  );

  function handleExportConfig() {
    const nextVersion = (config.configVersion ?? 1) + 1;
    const snippet = {
      configVersion: nextVersion,
      defaultSettings: {
        indicatorIds: selectedIndicators.map(i => i.id),
        enabledIds: [...currentEnabled],
        goals: { ...currentGoals },
        themes: currentThemes.map(t => ({ ...t, indicatorIds: [...t.indicatorIds] })),
        gradients: { ...currentGradients },
        rationales: { ...currentRationales },
        yearRange: { ...currentYearRange }
      }
    };
    const text = JSON.stringify(snippet, null, 2);
    const done = () => { setExportCopied(true); setTimeout(() => setExportCopied(false), 2500); };
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text).then(done).catch(() => fallbackCopy(text, done));
    } else {
      fallbackCopy(text, done);
    }
  }

  function fallbackCopy(text, done) {
    const el = document.createElement('textarea');
    el.value = text;
    el.style.position = 'fixed'; el.style.opacity = '0';
    document.body.appendChild(el);
    el.select();
    try { document.execCommand('copy'); done(); } catch (_) {}
    document.body.removeChild(el);
  }

  function handleOutcomesApply() {
    onOutcomesSave(outcomeDraft);
    onClose();
  }

  function handleCustomizeSave() {
    const orderedEnabledIds = outcomeDraft
      .filter(i => selected.has(i.id))
      .map(i => i.id);
    onCustomizeSave(
      orderedEnabledIds,
      goalInputs,
      gradientSelections,
      rationaleInputs,
      yearRange,
      themes,
      outcomeDraft
    );
    onClose();
  }

  return (
    <div
      className="ds-overlay"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="ds-modal">
        <div className="ds-header">
          <h2 className="ds-title">Dashboard Settings</h2>
          <button className="ds-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="ds-tabs">
          <button
            className={`ds-tab${activeTab === 'outcomes' ? ' ds-tab--active' : ''}`}
            onClick={() => setActiveTab('outcomes')}
          >
            Outcomes
          </button>
          <button
            className={`ds-tab${activeTab === 'customize' ? ' ds-tab--active' : ''}`}
            onClick={() => setActiveTab('customize')}
          >
            Customize
          </button>
          <button
            className={`ds-tab${activeTab === 'theme' ? ' ds-tab--active' : ''}`}
            onClick={() => setActiveTab('theme')}
          >
            Theme
          </button>
          <button
            className={`ds-tab${activeTab === 'intro' ? ' ds-tab--active' : ''}`}
            onClick={() => setActiveTab('intro')}
          >
            Intro Screen
          </button>
        </div>

        <div className="ds-body">
          {activeTab === 'outcomes' ? (
            <OutcomesTab
              draft={outcomeDraft}
              onDraftChange={setOutcomeDraft}
              census={census}
              cdc={cdc}
              svi={svi}
              ejscreen={ejscreen}
              education={education}
              catalogLoading={catalogLoading}
              censusError={censusError}
              cdcError={cdcError}
            />
          ) : activeTab === 'customize' ? (
            <CustomizeTab
              allIndicators={outcomeDraft}
              onReorder={setOutcomeDraft}
              selected={selected}
              setSelected={setSelected}
              goalInputs={goalInputs}
              setGoalInputs={setGoalInputs}
              gradientSelections={gradientSelections}
              setGradientSelections={setGradientSelections}
              rationaleInputs={rationaleInputs}
              setRationaleInputs={setRationaleInputs}
              yearRange={yearRange}
              setYearRange={setYearRange}
              allYears={allYears}
              themes={themes}
              setThemes={setThemes}
            />
          ) : activeTab === 'theme' ? (
            <ThemeTab
              selected={draftBrandTheme}
              onSelect={setDraftBrandTheme}
              custom={draftCustomBranding}
              onCustomChange={setDraftCustomBranding}
            />
          ) : (
            <IntroTab
              content={draftIntro}
              onChange={setDraftIntro}
            />
          )}
        </div>

        <div className="ds-footer">
          <button
            className="btn-export-config"
            onClick={handleExportConfig}
            title="Copy current settings as a config snippet — paste into fcp-pilot.js and deploy to make these the default for all browsers"
          >
            {exportCopied ? '✓ Copied!' : 'Export config'}
          </button>
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          {activeTab === 'outcomes' ? (
            <button
              className="btn-primary"
              disabled={outcomeDraft.length === 0}
              onClick={handleOutcomesApply}
            >
              Apply
            </button>
          ) : activeTab === 'customize' ? (
            <button
              className="btn-primary"
              disabled={selected.size === 0}
              onClick={handleCustomizeSave}
            >
              Save
            </button>
          ) : activeTab === 'theme' ? (
            <button
              className="btn-primary"
              onClick={() => { onThemeSave(draftBrandTheme, draftCustomBranding); onClose(); }}
            >
              Apply
            </button>
          ) : (
            <button
              className="btn-primary"
              onClick={() => { onIntroSave(draftIntro); onClose(); }}
            >
              Save
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
